/**
 * WoW class colors (Blizzard official hex values).
 */
export const CLASS_COLORS: Record<string, string> = {
  DEATHKNIGHT: '#C41E3A',
  DRUID:       '#FF7C0A',
  HUNTER:      '#AAD372',
  MAGE:        '#3FC7EB',
  PALADIN:     '#F48CBA',
  PRIEST:      '#FFFFFF',
  ROGUE:       '#FFF468',
  SHAMAN:      '#0070DD',
  WARLOCK:     '#8788EE',
  WARRIOR:     '#C69B6D',
};

export function classColor(classFile?: string): string {
  if (!classFile) return '#cccccc';
  return CLASS_COLORS[classFile.toUpperCase()] || '#cccccc';
}

/**
 * Parse a WoW item link: "|cffXXXXXX|Hitem:ID:ENCHANT:GEM1:GEM2:GEM3:GEM4:SUFFIX:UNIQUE:LINKLVL|h[Name]|h|r"
 */
export interface ParsedItemLink {
  id: number;
  enchant?: number;
  gems: number[];
  suffix?: number;
  name?: string;
  qualityColor?: string;   // "#a335ee" for epic etc.
  qualityName?: string;    // "Epic", "Rare", ...
}

// WoW item-link color-code → rarity
const QUALITY_BY_HEX: Record<string, { name: string; color: string }> = {
  '9d9d9d': { name: 'Poor',      color: '#9d9d9d' },
  'ffffff': { name: 'Common',    color: '#ffffff' },
  '1eff00': { name: 'Uncommon',  color: '#1eff00' },
  '0070dd': { name: 'Rare',      color: '#0070dd' },
  'a335ee': { name: 'Epic',      color: '#a335ee' },
  'ff8000': { name: 'Legendary', color: '#ff8000' },
  'e6cc80': { name: 'Heirloom',  color: '#e6cc80' },
  '00ccff': { name: 'Artifact',  color: '#00ccff' },
};

export function parseItemLink(link?: string): ParsedItemLink | null {
  if (!link) return null;

  // color prefix like |cffa335ee
  let qualityColor: string | undefined;
  let qualityName: string | undefined;
  const colorMatch = /\|c[fF]{2}([0-9a-fA-F]{6})/.exec(link);
  if (colorMatch) {
    const key = colorMatch[1].toLowerCase();
    const q = QUALITY_BY_HEX[key];
    if (q) {
      qualityColor = q.color;
      qualityName = q.name;
    } else {
      qualityColor = '#' + key;
    }
  }

  const m = /Hitem:(\d+):(\d+):(\d+):(\d+):(\d+):(\d+):(-?\d+)[^|]*\|h\[([^\]]+)\]/.exec(link);
  if (!m) {
    const m2 = /Hitem:(\d+).*\|h\[([^\]]+)\]/.exec(link);
    if (m2) return { id: +m2[1], gems: [], name: m2[2], qualityColor, qualityName };
    const m3 = /Hitem:(\d+)/.exec(link);
    if (!m3) return null;
    return { id: +m3[1], gems: [], qualityColor, qualityName };
  }
  return {
    id: +m[1],
    enchant: +m[2] || undefined,
    gems: [+m[3], +m[4], +m[5], +m[6]].filter((g) => g > 0),
    suffix: +m[7] || undefined,
    name: m[8],
    qualityColor,
    qualityName,
  };
}

/**
 * Build the Wowhead `data-wowhead` attribute for the WotLK domain.
 */
export function wowheadAttr(parsed: ParsedItemLink | null): string {
  if (!parsed) return '';
  const parts = [`item=${parsed.id}`];
  if (parsed.enchant) parts.push(`ench=${parsed.enchant}`);
  if (parsed.gems.length) parts.push(`gems=${parsed.gems.join(':')}`);
  parts.push('domain=wotlk');
  return parts.join('&');
}

/**
 * Refresh Wowhead tooltips after mount/update so newly added [data-wowhead]
 * elements get processed.
 */
export function refreshWowheadTooltips() {
  // @ts-ignore - injected by the script in index.html
  if (typeof window !== 'undefined' && (window as any).$WowheadPower) {
    // @ts-ignore
    (window as any).$WowheadPower.refreshLinks?.();
  }
}

/**
 * WotLK equipment slot layout (slotId -> column, row) for a paper-doll grid.
 */
export const SLOT_LAYOUT: Record<number, { col: 'left' | 'right' | 'bottom'; order: number; name: string }> = {
  1:  { col: 'left',  order: 1,  name: 'Head' },
  2:  { col: 'left',  order: 2,  name: 'Neck' },
  3:  { col: 'left',  order: 3,  name: 'Shoulder' },
  15: { col: 'left',  order: 4,  name: 'Back' },
  5:  { col: 'left',  order: 5,  name: 'Chest' },
  4:  { col: 'left',  order: 6,  name: 'Shirt' },
  19: { col: 'left',  order: 7,  name: 'Tabard' },
  9:  { col: 'left',  order: 8,  name: 'Wrist' },

  10: { col: 'right', order: 1,  name: 'Hands' },
  6:  { col: 'right', order: 2,  name: 'Waist' },
  7:  { col: 'right', order: 3,  name: 'Legs' },
  8:  { col: 'right', order: 4,  name: 'Feet' },
  11: { col: 'right', order: 5,  name: 'Finger 1' },
  12: { col: 'right', order: 6,  name: 'Finger 2' },
  13: { col: 'right', order: 7,  name: 'Trinket 1' },
  14: { col: 'right', order: 8,  name: 'Trinket 2' },

  16: { col: 'bottom', order: 1, name: 'Main Hand' },
  17: { col: 'bottom', order: 2, name: 'Off Hand' },
  18: { col: 'bottom', order: 3, name: 'Ranged' },
};

/**
 * Build a Wowhead icon URL from a WoW texture path.
 *   Input:  "Interface\Icons\INV_Chest_Cloth_16"  (forward or back slashes)
 *   Output: "https://wow.zamimg.com/images/wow/icons/large/inv_chest_cloth_16.jpg"
 */
export function iconUrl(texture?: string, size: 'large' | 'medium' | 'small' = 'large'): string | null {
  if (!texture) return null;
  // Pull the filename off either slash direction
  const name = texture.replace(/\\/g, '/').split('/').pop();
  if (!name) return null;
  return `https://wow.zamimg.com/images/wow/icons/${size}/${name.toLowerCase()}.jpg`;
}

/**
 * Faction accent colors (subtle — used as borders/stripes, not page-wide).
 */
export function factionColor(faction?: string): string {
  if (!faction) return '#666';
  if (/alliance/i.test(faction)) return '#4a7dd4';
  if (/horde/i.test(faction)) return '#c43a3a';
  return '#666';
}

/**
 * Resolve the active spec for a snapshot.
 *  - Uses talents.activeSpec when present.
 *  - Otherwise picks the talent tab with the most points spent.
 * Returns the tab name + icon URL (already Wowhead-resolved) or null
 * when there are no talents to inspect.
 */
import type { Snapshot, TalentTab } from '../types';
export function specOf(snapshot: Snapshot): { name: string; iconUrl: string | null; pointsSpent: number } | null {
  const tabs = snapshot.talents?.tabs;
  if (!tabs) return null;
  const entries = Object.entries(tabs) as Array<[string, TalentTab]>;
  if (entries.length === 0) return null;

  let chosen: TalentTab | null = null;

  // 1) Honor activeSpec when it points at a real tab.
  const active = snapshot.talents?.activeSpec;
  if (typeof active === 'number') {
    const direct = tabs[String(active)];
    if (direct) chosen = direct;
  }

  // 2) Fall back to "most points spent".
  if (!chosen) {
    chosen = entries
      .map(([, t]) => t)
      .reduce((best, t) => (!best || (t.pointsSpent ?? 0) > (best.pointsSpent ?? 0) ? t : best),
              null as TalentTab | null);
  }
  if (!chosen) return null;
  return {
    name: chosen.name,
    iconUrl: iconUrl(chosen.icon),
    pointsSpent: chosen.pointsSpent ?? 0,
  };
}

/** A snapshot is "stale" if its updatedAt is older than `days` days. */
export function isStale(updatedAtMs: number, days = 14): boolean {
  return Date.now() - updatedAtMs > days * 86_400_000;
}

export function formatMoney(copper?: number): string {
  if (!copper) return '0c';
  const g = Math.floor(copper / 10000);
  const s = Math.floor((copper % 10000) / 100);
  const c = copper % 100;
  return `${g}g ${s}s ${c}c`;
}

/**
 * Equipment normalizer. The upstream Lua parser can produce either an object
 * keyed by slot id (when Blizzard's serializer wrote `[k]=v`) or an array
 * indexed 0-based (when it wrote a dense sequence). Re-key by each item's
 * `slot` field so downstream code always sees `Record<string, ItemSlot>`.
 */
import type { ItemSlot as ItemSlotT } from '../types';
export function normalizeEquipment(
  eq: unknown
): Record<string, ItemSlotT> {
  if (!eq) return {};
  const out: Record<string, ItemSlotT> = {};
  const entries: Array<[unknown, unknown]> = Array.isArray(eq)
    ? (eq as unknown[]).map((v, i) => [i, v])
    : Object.entries(eq as Record<string, unknown>);
  for (const [, item] of entries) {
    if (!item || typeof item !== 'object') continue;
    const id = (item as ItemSlotT).slot;
    if (typeof id !== 'number') continue;
    out[String(id)] = item as ItemSlotT;
  }
  return out;
}
